import React, {useState, useEffect} from 'react';
import {connect} from 'react-redux';
import {updateModalContent} from "./redux/FeedbackRedux";
import Modal from "./Modal";
import FormDropdown from "./v2/FormDropdown";
import TimeframeRangeLabel from "./TimeframeRangeLabel";
import { timeframeRangeText } from "./helpers/timeframeRange";

/**
 * Build a path from segments with exactly one slash between them (no //, no trailing slash).
 * @param {...string} segments - Path segments (leading/trailing slashes are stripped)
 * @returns {string} Normalized path with single leading slash
 */
function joinPath(...segments) {
    const parts = segments
        .filter(Boolean)
        .map((s) => String(s).replace(/^\/+|\/+$/g, ''))
        .filter(Boolean);
    return '/' + parts.join('/').replace(/\/+/g, '/');
}

/**
 * Get the AI reports base path (no slug, no trailing slash) from current pathname.
 * Strips the last path segment (the slug) if the URL contains /ai-reports/{slug}.
 */
function getAiReportBasePath() {
    const pathname = window.location.pathname.replace(/\/$/, '');
    const match = pathname.match(/^(\/.*\/ai-reports)\/.+$/);
    return match ? match[1] : pathname;
}

function groupAiReportOptions(aiReportOptions) {
    return (aiReportOptions || []).reduce((groups, option) => {
        const groupKey = option.group || 'custom';
        if (!groups[groupKey]) {
            groups[groupKey] = [];
        }
        groups[groupKey].push(option);

        return groups;
    }, {});
}

const TIMEFRAME_OPTIONS = [
    { text: __('Today'), id: 'today' },
    { text: __('Yesterday'), id: 'yesterday' },
    { text: __('Week to Date'), id: 'weekToDate' },
    { text: __('Month to Date'), id: 'monthToDate' },
    { text: __('Last 30 Days'), id: 'last30' },
    { text: __('Lifetime'), id: 'lifetime' },
    { text: __('Custom'), id: 'custom' },
];

function timeframeLabel(id) {
    const option = TIMEFRAME_OPTIONS.find(o => o.id === id);
    return option ? option.text : id;
}

/**
 * Builds the {start_date, end_date} payload sent to the custom AI report endpoint.
 *
 * NOTE (RTF-4257): this intentionally differs from the range shown next to the
 * dropdown (TimeframeRangeLabel / timeframeRangeSuffix). This function defines the
 * dates the *report* actually covers — browser-local, Sunday week-start, and a fixed
 * 'lifetime' floor of 2020-01-01. The label beside the dropdown instead shows the
 * canonical, backend-aligned range (business timezone, Monday week-start, lifetime =
 * business created_at).
 * They can disagree by a day (week-start) or more (lifetime). Consolidating the two
 * is deliberately out of scope here — changing this would alter report coverage.
 */
function timeframeToDates(timeframe) {
    const today = new Date();
    const fmt = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');

    switch (timeframe) {
        case 'today':
            return { start_date: fmt(today), end_date: fmt(today) };
        case 'yesterday': {
            const y = new Date(today);
            y.setDate(y.getDate() - 1);
            return { start_date: fmt(y), end_date: fmt(y) };
        }
        case 'weekToDate': {
            const start = new Date(today);
            start.setDate(start.getDate() - start.getDay());
            return { start_date: fmt(start), end_date: fmt(today) };
        }
        case 'monthToDate': {
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            return { start_date: fmt(start), end_date: fmt(today) };
        }
        case 'last30': {
            const start = new Date(today);
            start.setDate(start.getDate() - 30);
            return { start_date: fmt(start), end_date: fmt(today) };
        }
        case 'lifetime': {
            return { start_date: '2020-01-01', end_date: fmt(today) };
        }
        default:
            return { start_date: fmt(today), end_date: fmt(today) };
    }
}

function todayStr() {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

const CustomReportModal = ({ customReportUrl, userEmail, show, onClose, business }) => {
    const [reportName, setReportName] = useState('');
    const [timeframe, setTimeframe] = useState('last30');
    const [customStartDate, setCustomStartDate] = useState(todayStr());
    const [customEndDate, setCustomEndDate] = useState(todayStr());
    const [includePublicReviews, setIncludePublicReviews] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const resetForm = () => {
        setReportName('');
        setTimeframe('last30');
        setCustomStartDate(todayStr());
        setCustomEndDate(todayStr());
        setIncludePublicReviews(true);
        setErrorMessage('');
        setSuccessMessage('');
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleGenerate = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');
        setIsSubmitting(true);

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

        try {
            const response = await fetch(customReportUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                },
                body: JSON.stringify({
                    ...(timeframe === 'custom'
                        ? { start_date: customStartDate, end_date: customEndDate }
                        : timeframeToDates(timeframe)),
                    report_name: reportName || null,
                    include_public_reviews: includePublicReviews,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                const msg = data?.error?.message || data?.message || __('An error occurred. Please try again.');
                setErrorMessage(msg);
            } else {
                setSuccessMessage(
                    __('Generating the report could take a few minutes.') + ' ' +
                    __('An email will be sent to') + ' ' + (userEmail || '') + ' ' +
                    __('when the report is ready.')
                );
            }
        } catch (err) {
            setErrorMessage(__('An error occurred. Please try again.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!show) return null;

    return (
        <div className="custom-report-modal-backdrop" onClick={handleClose}>
            <div className="custom-report-modal" onClick={(e) => e.stopPropagation()}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="mb-0">{__('Create a custom AI summary report')}</h5>
                    <button type="button" className="close" onClick={handleClose}>
                        <span>&times;</span>
                    </button>
                </div>

                {successMessage ? (
                    <>
                        <div className="alert alert-success py-2 mb-3">{successMessage}</div>
                        <div className="d-flex mt-3">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleClose}
                            >
                                {__('Done')}
                            </button>
                        </div>
                    </>
                ) : (
                    <form onSubmit={handleGenerate}>
                        <div className="form-group mb-3">
                            <label htmlFor="customReportName">{__('Report Name')} <span className="text-muted">({__('Optional')})</span></label>
                            <input
                                type="text"
                                className="form-control"
                                id="customReportName"
                                placeholder={__('e.g. Friday Mav Game')}
                                value={reportName}
                                onChange={(e) => setReportName(e.target.value)}
                                maxLength={255}
                            />
                        </div>

                        <div className="form-group mb-3">
                            <label>{__('Time Period')}</label>
                            <div className="d-flex align-items-center flex-wrap">
                                <div style={{ minWidth: '180px' }} className="mr-2">
                                    <FormDropdown
                                        classes="custom-report-timeframe-dropdown"
                                        buttonClasses="p-1 bg-white"
                                        menuClasses=""
                                        title={timeframeLabel(timeframe)}
                                        items={TIMEFRAME_OPTIONS.map(option => ({
                                            ...option,
                                            range: timeframeRangeText(
                                                option.id,
                                                business?.call_at_timezone,
                                                business?.created_at
                                            )
                                        }))}
                                        itemOnClickFunction={(e) => {
                                            const id = e.target.closest('[id]')?.id || e.target.textContent;
                                            const option = TIMEFRAME_OPTIONS.find(o => o.id === id || o.text === id);
                                            if (option) setTimeframe(option.id);
                                        }}
                                    />
                                </div>
                                <TimeframeRangeLabel
                                    timeframe={timeframe}
                                    timezone={business?.call_at_timezone}
                                    lifetimeStart={business?.created_at}
                                />
                            </div>
                        </div>

                        {timeframe === 'custom' && (
                            <div className="form-row mb-3 custom-report-custom-dates">
                                <div className="form-group col-md-6 mb-0">
                                    <label>{__('Start Date')}</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={customStartDate}
                                        onChange={(e) => setCustomStartDate(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group col-md-6 mb-0">
                                    <label>{__('End Date')}</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={customEndDate}
                                        onChange={(e) => setCustomEndDate(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        <div className="form-check mb-3">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="includePublicReviews"
                                checked={includePublicReviews}
                                onChange={(e) => setIncludePublicReviews(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="includePublicReviews">
                                {__('Include Public Reviews')}
                            </label>
                        </div>

                        {errorMessage && (
                            <div className="alert alert-danger py-2 mb-3">{errorMessage}</div>
                        )}

                        <div className="d-flex mt-3">
                            <button
                                type="submit"
                                className="btn btn-primary mr-2"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? __('Generating...') : __('Generate')}
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={handleClose}
                                disabled={isSubmitting}
                            >
                                {__('Cancel')}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

const FeedbackLink = ({ feedback }) => (
    <li>
        <a href={feedback?.url} target="_blank" rel="noopener noreferrer">{feedback?.description}
            <svg viewBox="0 0 24 24" fill="none">
                <path
                    d="M10 5H8.2c-1.12 0-1.68 0-2.108.218a1.999 1.999 0 0 0-.874.874C5 6.52 5 7.08 5 8.2v7.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874c.427.218.987.218 2.105.218h7.606c1.118 0 1.677 0 2.104-.218.377-.192.683-.498.875-.874.218-.428.218-.987.218-2.105V14m1-5V4m0 0h-5m5 0-7 7"
                    stroke="#373c40" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        </a>
    </li>
);

const AISummaryRightBlock = ({ item, index, isIssues }) => {
    const [showAllFeedbacks, setShowAllFeedbacks] = useState(false);

    useEffect(() => {
        setShowAllFeedbacks(false);
    }, [index, isIssues]);

    if (!item) {
        return null;
    }

    const potentialSolutionsKey = item['potential_solutions'] ? 'potential_solutions' : 'potential_insights';

    const filteredFeedbacks = (item['feedbacks'] || []).filter(feedback => feedback?.description);
    const hasMoreFeedbacks = filteredFeedbacks.length > 3;
    const firstThree = filteredFeedbacks.slice(0, 3);
    const remaining = filteredFeedbacks.slice(3);

    const filteredReviews = item['reviews']
        ? Object.values(item['reviews']).filter(feedback => feedback?.description).slice(0, 3)
        : [];

    return (
        <div className="aisummary-summary-text-wrapper mt-3" data-id={index}>
            <h5 className="border-bottom">{ item.title }</h5>

            <h6>{ __('Summary') }</h6>
            <p>{ item.explanation }</p>

            <h6>{ isIssues ? __('Potential Solutions') : __('Potential Insights') }</h6>
            { item[potentialSolutionsKey].map((solution, index) => {
                return (
                    <p key={index}>{ solution }</p>
                )
            })}

            {/* Example Feedbacks */}
            {filteredFeedbacks.length > 0 && <h6>{ __('Example Feedbacks') }</h6>}
            {filteredFeedbacks.length > 0 && (
                <ul>
                    {firstThree.map((feedback, i) => (
                        <FeedbackLink key={i} feedback={feedback} />
                    ))}
                </ul>
            )}

            {hasMoreFeedbacks && (
                <span
                    className="aisummary-toggle-link"
                    onClick={() => setShowAllFeedbacks(!showAllFeedbacks)}
                >
                    {showAllFeedbacks
                        ? <>{__('show less')} <i className="fas fa-chevron-down"></i></>
                        : <>{__('show all')} <i className="fas fa-chevron-right"></i></>
                    }
                </span>
            )}

            {showAllFeedbacks && remaining.length > 0 && (
                <ul>
                    {remaining.map((feedback, i) => (
                        <FeedbackLink key={i + 3} feedback={feedback} />
                    ))}
                </ul>
            )}

            {/* Example Reviews */}
            {filteredReviews.length > 0 && <h6>{ __('Example Public Reviews') }</h6>}
            {filteredReviews.length > 0 && (
                <ul>
                    {filteredReviews.map((feedback, index) => (
                        <li key={index}>
                            <a href={feedback.url} target="_blank" rel="noopener noreferrer">
                                {feedback.description}
                                <svg viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M10 5H8.2c-1.12 0-1.68 0-2.108.218a1.999 1.999 0 0 0-.874.874C5 6.52 5 7.08 5 8.2v7.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874c.427.218.987.218 2.105.218h7.606c1.118 0 1.677 0 2.104-.218.377-.192.683-.498.875-.874.218-.428.218-.987.218-2.105V14m1-5V4m0 0h-5m5 0-7 7"
                                        stroke="#373c40" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </a>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
};

class AISummary extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            showIssues: this.props.issueStories && Object.keys(this.props.issueStories).length > 0,
            selectedIndex: 0,
            showCustomModal: false,
        }

        this.selectRef = React.createRef();
        this.handleEmailClick = this.handleEmailClick.bind(this);
        this.sortItemsAsc = this.sortItemsAsc.bind(this);
    }

    // get the data based on the selected tab
    itemsData = () => {
        let itemData;

        if (this.state.showIssues) {
            itemData = this.props.issueStories?.issues;
        } else {
            itemData = this.props.successStories?.success_stories;
        }

        if (!itemData || typeof itemData !== "object") return []; // Ensure it's an object before processing

        //
        return this.sortItemsAsc(itemData);
    };

    sortItemsAsc = (items) => {
        return Object.entries(items)
            .sort(([, a], [, b]) => {
                const aFeedbacks = a.feedbacks ? Object.keys(a.feedbacks).length : 0;
                const aReviews = a.reviews ? Object.keys(a.reviews).length : 0;
                const bFeedbacks = b.feedbacks ? Object.keys(b.feedbacks).length : 0;
                const bReviews = b.reviews ? Object.keys(b.reviews).length : 0;

                const aTotal = aFeedbacks + aReviews;
                const bTotal = bFeedbacks + bReviews;

                if (bTotal !== aTotal) {
                    return bTotal - aTotal; // Sort by total sum (descending)
                }

                return bFeedbacks - aFeedbacks; // If sum is equal, sort by feedbacks (descending)
            })
            .map(([key, value]) => ({ id: key, ...value })); // Convert back to array format
    }

    // forward modal
    handleEmailClick(event) {
        event.preventDefault();
        const modal_id = "emailForwardModal";
        const modal_title = __("AI Summary Monthly Recipents");
        const modal_body = "renderEmailForwardSummary";
        const button_1_class = "outline-primary";
        const button_1_id = "cancel";
        const button_1_text = __("Cancel");
        const button_2_class = "primary";
        const button_2_id = "cancel";
        const button_2_text = __("Close");

        this.props.dispatch(updateModalContent({
            modal_id: modal_id,
            modal_title: modal_title,
            modal_body: modal_body,
            button_1_class: button_1_class,
            button_1_id: button_1_id,
            button_1_text: button_1_text,
            button_2_class: button_2_class,
            button_2_id: button_2_id,
            button_2_text: button_2_text
        }));
        jQuery('.modal:not(.error)').modal();
    }

    emptyStateMessage() {
        const periodLabel = this.props.emptyStatePeriodLabel;

        switch (this.props.emptyStateReason) {
            case 'in_progress':
                return periodLabel
                    ? __("Your AI Report for :period is being generated. You'll receive an email when it's ready.", { ':period': periodLabel })
                    : __("Your AI Report is being generated. You'll receive an email when it's ready.");
            case 'failed':
                return periodLabel
                    ? __('The AI Report for :period failed to generate. You can request a new one with Create Custom Report.', { ':period': periodLabel })
                    : __('The last AI Report attempt failed. You can request a new one with Create Custom Report.');
            case 'no_eligible_feedbacks':
                return periodLabel
                    ? __("No AI Report for :period: there wasn't enough eligible feedback to analyse.", { ':period': periodLabel })
                    : __("No AI Report: there wasn't enough eligible feedback to analyse.");
            default:
                return null;
        }
    }

    renderLatestAttemptNotice() {
        const message = this.emptyStateMessage();

        return message ? <div className="alert alert-warning mx-3 mt-3 mb-0" role="alert">{message}</div> : null;
    }

    renderEmptyState() {
        if (this.props.currentAiReportSlug) {
            // a completed report with no stories: the reason belongs to another attempt, so it stays a notice
            return (
                <>
                    {this.renderLatestAttemptNotice()}
                    <h5 className='aisummary-nodata'>{__('AI Report not yet available')}</h5>
                </>
            );
        }

        switch (this.props.emptyStateReason) {
            case 'in_progress':
            case 'failed':
                return <h5 className='aisummary-nodata'>{this.emptyStateMessage()}</h5>;
            case 'no_eligible_feedbacks':
                return (
                    <>
                        <h5 className='aisummary-nodata'>{this.emptyStateMessage()}</h5>
                        <p className="aisummary-nodata-hint text-muted">{__('Reports are generated monthly. Use Create Custom Report to request one for a different date range.')}</p>
                    </>
                );
            case 'none':
                return (
                    <>
                        <h5 className='aisummary-nodata'>{__('AI Report not yet available')}</h5>
                        <p className="aisummary-nodata-hint text-muted">{__('Reports are generated monthly. Use Create Custom Report to request one now.')}</p>
                    </>
                );
            default:
                return <h5 className='aisummary-nodata'>{__('AI Report not yet available')}</h5>;
        }
    }

    render() {
        const items = this.itemsData();

        // construct the intro
        const businessName = this.props.business.name;
        const location = this.props.business.city;
        const startDate = this.props.reportStartDateFormatted;
        const endDate = this.props.reportEndDateFormatted;
        const reportName = this.props.aiReportName;
        const feedbackCount = this.props.aiSummaryTotalFeedbacks;
        const reviewsCount = this.props.aiSummaryTotalReviews;

        const analysisText = __(
            `This analysis covers <strong>${feedbackCount} Feedbacks</strong> ${
                reviewsCount > 0 ? `and <strong>${reviewsCount} Public Reviews </strong>` : ''
            } received for <strong>${businessName} ${location}</strong> collected between <strong>${startDate}</strong> and <strong>${endDate}</strong>${reportName ? ` (${reportName})` : ''}.`
        );

        const currentReportSlug = this.props.currentAiReportSlug || this.props.aiReportOptions?.[0]?.slug || '';
        const groupedAiReportOptions = groupAiReportOptions(this.props.aiReportOptions);

        const basePath = getAiReportBasePath();
        const PDFurl = currentReportSlug ? joinPath(basePath, currentReportSlug, 'pdf') : '';

        return (
            <div className="w-100 right-side-content">
                <div className="main-section">
                    <div className="w-100 p-2 bg-white border-bottom shadow-sm d-flex align-items-center p-3">
                        <p className='mb-0 w-100'>{__('On this page you can access AI-generated summaries of all Feedback received during the selected time period. Our in-depth analysis highlights trending issues, provides specific Feedback examples of customer comments, and offers tailored recommendations for improvement. You can explore historical summaries across different date ranges, allowing you to track changes over time and gain valuable insights into customer satisfaction.')}.</p>
                    </div>

                    <div className="col-12 mt-3">
                        {/* Content */}
                        {this.props.isManager &&
                            <div className="card flex-row align-items-center pt-3 pb-3">
                                <h5 className="pl-3 pr-3 mb-0">{__('Monthly AI Summary Recipients')}:</h5>

                                <button
                                    onClick={this.handleEmailClick}
                                    className={'btn btn-primary ml-3'}
                                >
                                    {__('Manage Recipients')}
                                </button>
                            </div>
                        }

                        {/* Business Address */}
                        <div className="col-sm-12 mt-3 p-0">
                            <div id='report_body' className="card col-sm-12 p-0 mt-3">
                                <div className="title-row border-bottom">
                                    <div className="card-title tab">
                                        <h5 className="pl-3 pr-3">{__('AI Summary')}</h5>
                                    </div>
                                    <p className="aisummary-address mb-0 pl-3 pr-3 pb-0">
                                        {this.props.business['street_address']}, {this.props.business['city']}, {this.props.business['state']}, {this.props.business['zip_code']}
                                    </p>

                                </div>

                                {/* Report Summary */}
                                {(this.props.aiSummaryTotalFeedbacks > 0 || this.props.aiSummaryTotalReviews > 0) &&
                                    <p className="px-3 py-2 summary-intro-p" dangerouslySetInnerHTML={{ __html: analysisText }} />
                                }
                            </div>
                        </div>
                    </div>

                    {/* Analysis */}
                    <div className="w-100 justify-content-center">
                        <div className="col-sm-12 mt-3">
                            <div className="card col-sm-12 p-0 mt-3 <?= $isWeb ? 'web-view' : '' ?>">
                                {/* Top Bar */}
                                <div className="title-row my-3">
                                    <div className="aisummary-toggle card-title">
                                        <div className="aisummary-toggle-wrapper" data-issues={this.state.showIssues}>
                                            {(this.props.issueStories && Object.keys(this.props.issueStories).length > 0) &&
                                                <button onClick={() => {
                                                    this.setState({selectedIndex: 0});
                                                    this.setState({showIssues: true})
                                                }}>
                                                    <i className="fas fa-thumbs-down text-danger mr-1"></i>
                                                    {__('Issues Analysis')}
                                                </button>
                                            }

                                            {(this.props.successStories && Object.keys(this.props.successStories).length > 0) &&
                                                <button onClick={() => {
                                                    this.setState({selectedIndex: 0});
                                                    this.setState({showIssues: false})
                                                }}>
                                                    <i className="fas fa-thumbs-up text-success mr-1"></i>
                                                    {__('Success Stories Analysis')}
                                                </button>
                                            }
                                        </div>
                                    </div>

                                    <div className="aisummary-actions card-title mr-0 ml-auto">
                                        <select
                                            onChange={(e) => {
                                                const selectedValue = e.target.value;
                                                const newPath = joinPath(getAiReportBasePath(), selectedValue);
                                                window.location.href = newPath;
                                            }}
                                            id="aisummary-archive"
                                            className="mr-3"
                                            value={currentReportSlug}
                                        >
                                            <option value="" disabled>{ __('Select a report') }</option>
                                            {['custom', 'monthly'].map((group) => {
                                                const options = groupedAiReportOptions[group];
                                                const groupLabel = group === 'monthly' ? __('Monthly') : __('Custom');

                                                return (
                                                    <optgroup
                                                        key={group}
                                                        label={groupLabel}
                                                    >
                                                        {options?.length ? options.map((option) => (
                                                            <option key={option.slug} value={option.slug}>
                                                                {option.label}
                                                            </option>
                                                        )) : (
                                                            <option disabled>{__('No custom reports available')}</option>
                                                        )}
                                                    </optgroup>
                                                );
                                            })}
                                        </select>

                                        <button
                                            type="button"
                                            className="btn btn-outline-primary mr-2"
                                            onClick={() => this.setState({ showCustomModal: true })}
                                        >
                                            <i className="fas fa-plus mr-1"></i>{__('Create Custom Report')}
                                        </button>

                                        {PDFurl && (
                                        <a
                                            href={PDFurl}
                                            target="_blank"
                                            className='run-report btn btn-primary mr-2'
                                            id="download-pdf-alt"
                                        ><i
                                            className="fas fa-file-download mr-1"></i> {__('Download PDF')}</a>
                                        )}
                                    </div>
                                </div>

                                {/* Analysis Section */}
                                <div className="aisummary-summary m-3" data-issues={this.state.showIssues}>
                                    {!(this.props.successStories.length === 0 && this.props.issueStories.length === 0) && this.renderLatestAttemptNotice()}
                                    {this.props.successStories.length === 0 && this.props.issueStories.length === 0 && this.renderEmptyState()}

                                    {this.props.successStories['summary']?.length > 0 &&
                                        <div className="aisummary-summary-success">
                                            <h5>{__('Success Story Analysis')}</h5>
                                            <p>{this.props.successStories['summary']}</p>
                                        </div>
                                    }

                                    {this.props.issueStories['summary']?.length > 0 &&
                                        <div className="aisummary-summary-issues">
                                            <h5>{__('Issues Analysis')}</h5>
                                            <p>{this.props.issueStories['summary']}</p>
                                        </div>
                                    }
                                </div>

                                {/* Content */}
                                {this.props.successStories.length !== 0 && this.props.issueStories.length !== 0 && (
                                    <div className="aisummary-content-wrapper" data-issues="true">
                                    <div className="aisummary-content aisummary-content-issues">
                                        {/* Analysis Content */}
                                        <ul className="aisummary-terms-list m-3">
                                            {items &&
                                                <li className="aisummary-terms-list-item aisummary-terms-list-header">
                                                    <span>{this.state.showIssues
                                                        ? this.props.issueStories['issues'].length > 0
                                                            ? __('Issues')
                                                            : ''
                                                        : this.props.successStories['success_stories']?.length > 0
                                                            ? __('Success Stories')
                                                            : ''
                                                    }</span>

                                                    <span>
                                                        {this.state.showIssues
                                                            ? this.props.issueStories['issues'].length > 0
                                                                ? __('Number of Mentions')
                                                                : ''
                                                            : this.props.successStories['success_stories']?.length > 0
                                                                ? __('Number of Mentions')
                                                                : ''
                                                        }
                                                        <small className="ml-2">{ __('(Feedbacks - Public Reviews)') }</small>
                                                        <i
                                                            className="far fa-question-circle text-muted ml-2"
                                                            data-toggle="tooltip"
                                                            data-placement="top"
                                                            data-title="{{ __('Feedbacks are private comments sent through the :platform platform. Public Reviews are collected from online public review sites where customers have left reviews.', ['platform' => tenant()->settings->companyName]) }}"
                                                        ></i>
                                                    </span>

                                                </li>
                                            }

                                            {items && items.map((issue, index) => {
                                                return (
                                                    <li
                                                        className="aisummary-terms-list-item"
                                                        onClick={() => {
                                                            this.setState({selectedIndex: index})

                                                        }}
                                                        data-active={this.state.selectedIndex === index}
                                                        key={index}
                                                    >
                                                        <span>{ issue['title'] }</span>
                                                        <span>{ issue['feedbacks'] ? issue['feedbacks'].length : '0' } - { issue.reviews ? Object.keys(issue.reviews).length : '0' }</span>
                                                    </li>
                                                )
                                            })}
                                        </ul>

                                        {/* Item Text */}
                                        {((Array.isArray(this.props.issueStories['issues']) && this.props.issueStories['issues'].length > 0) ||
                                                (Array.isArray(this.props.successStories['success_stories']) && this.props.successStories['success_stories'].length > 0)) &&
                                            <AISummaryRightBlock
                                                item={this.state.showIssues
                                                    ? this.props.issueStories['issues'] ? this.sortItemsAsc(this.props.issueStories['issues'])[this.state.selectedIndex] : null
                                                    : this.sortItemsAsc(this.props.successStories['success_stories'])[this.state.selectedIndex]}
                                                index={this.state.selectedIndex}
                                                isIssues={this.state.showIssues}
                                            />
                                        }

                                    </div>
                                </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {this.props.state.modal !== null &&
                    <Modal
                        modal={this.props.state.modal}
                        existingRecipients={this.props.existingRecipients}
                        contacts={this.props.contacts}
                        employees={this.props.employees}
                    />
                }

                <CustomReportModal
                    customReportUrl={this.props.customReportUrl}
                    userEmail={this.props.userEmail}
                    business={this.props.business}
                    show={this.state.showCustomModal}
                    onClose={() => this.setState({ showCustomModal: false })}
                />
            </div>
        )
    }
}

function mapStateToProps(state) {
    return {
        state: state,
        business: state.business,
        issueStories: state.issueStories,
        successStories: state.successStories,
        existingRecipients: state.existingRecipients,
        contacts: state.contacts,
        employees: state.employees,
        aiReportOptions: state.aiReportOptions,
        currentAiReportSlug: state.currentAiReportSlug,
        emptyStateReason: state.emptyStateReason,
        emptyStatePeriodLabel: state.emptyStatePeriodLabel,
        aiReportName: state.aiReportName,
        reportStartDate: state.reportStartDate,
        reportEndDate: state.reportEndDate,
        totalIssuesMentions: state.totalIssuesMentions,
        totalIssuesReviewMentions: state.totalIssuesReviewMentions,
        totalSuccessStoriesMentions: state.totalSuccessStoriesMentions,
        totalSuccessReviewStoriesMentions: state.totalSuccessReviewStoriesMentions,
        reportStartDateFormatted: state.reportStartDateFormatted,
        reportEndDateFormatted: state.reportEndDateFormatted,
        isManager: state.isManager,
        customReportUrl: state.customReportUrl,
        userEmail: state.userEmail,
        aiSummaryTotalFeedbacks: state.aiSummaryTotalFeedbacks,
        aiSummaryTotalReviews: state.aiSummaryTotalReviews,
    }
}

export default connect(mapStateToProps)(AISummary)
